import { useCallback, useImperativeHandle, forwardRef } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import tw, { styled } from 'twin.macro';
import { useAtom } from 'jotai';

import { urlObjectToUriString } from '@restworld/utils-common';

import {
  JobListingJpStatusFilter,
  JobListingLocationFilter,
  JobListingRestaurantNameFilter,
} from './employer-job-listing-filter.components';
import {
  employerJobListingJpStatusFilterAtom,
  employerJobListingLocationFilterAtom,
  employerJobListingRestaurantNameFilterAtom,
} from '../employer-job-listing.atoms';
import { useToggleEmployerFiltersModal } from '../../employer-filters-modal';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';

interface EmployerRestuarantFiltersFormValues {
  jpStatus: string[];
  location: string;
  restaurantName: string;
}
export interface IEmployerFilterForm {
  applyFilters: () => void;
  removeFilters: () => void;
}

const EmployerJobListingFilters = forwardRef<IEmployerFilterForm>((_, ref) => {
  const methods = useForm<EmployerRestuarantFiltersFormValues>();
  const { handleSubmit } = methods;
  const [, setEmployerJobListingJpStatusFilter] = useAtom(employerJobListingJpStatusFilterAtom);
  const [, setEmployerJobListingLocationFilter] = useAtom(employerJobListingLocationFilterAtom);
  const [, setEmployerJobListingRestaurantNameFilter] = useAtom(employerJobListingRestaurantNameFilterAtom);
  const { closeEmployerFiltersModal: closeModal } = useToggleEmployerFiltersModal();
  const router = useV2Router();
  const onSubmit: SubmitHandler<EmployerRestuarantFiltersFormValues> = useCallback(
    (values) => {
      setEmployerJobListingJpStatusFilter(values.jpStatus);
      setEmployerJobListingLocationFilter(values.location);
      setEmployerJobListingRestaurantNameFilter(values.restaurantName);
      router.push(
        urlObjectToUriString({
          pathname: router.pathname,
          query: { ...values },
        }),
        null,
        {
          shallow: true,
        }
      );
      closeModal();
    },
    [
      closeModal,
      router,
      setEmployerJobListingJpStatusFilter,
      setEmployerJobListingLocationFilter,
      setEmployerJobListingRestaurantNameFilter,
    ]
  );

  const handleRemove = useCallback(() => {
    setEmployerJobListingJpStatusFilter([]);
    setEmployerJobListingLocationFilter('');
    setEmployerJobListingRestaurantNameFilter('');
    closeModal();
    router.push(urlObjectToUriString({ pathname: router.pathname, query: null }), null, { shallow: true });
  }, [
    closeModal,
    router,
    setEmployerJobListingJpStatusFilter,
    setEmployerJobListingLocationFilter,
    setEmployerJobListingRestaurantNameFilter,
  ]);

  useImperativeHandle(ref, () => ({
    applyFilters: handleSubmit(onSubmit),
    removeFilters: handleRemove,
  }));

  return (
    <FiltersWrapper>
      <FormProvider {...methods}>
        <form action="post" tw="flex flex-col justify-between h-full gap-8 mt-8">
          <InputsWrapper>
            <JobListingLocationFilter />
            <JobListingRestaurantNameFilter />
            <JobListingJpStatusFilter />
          </InputsWrapper>
        </form>
      </FormProvider>
    </FiltersWrapper>
  );
});

export default EmployerJobListingFilters;

export const computeJobListingFiltersCount = ({
  restaurantName,
  jpStatus,
  location,
}: {
  restaurantName?: string;
  jpStatus?: string[];
  location?: string;
}) => {
  let count = 0;
  if (restaurantName) count += 1;
  if (jpStatus && jpStatus.length > 0) count += 1;
  if (location) count += 1;
  return count;
};

const FiltersWrapper = styled.div`
  ${tw`flex flex-col  h-full`}
`;
const InputsWrapper = styled.div`
  ${tw`flex flex-col gap-8`}
`;
